import {
    Box,
    Card,
    CardBody,
    Flex,
    Image,
    Stat,
    StatLabel,
    StatNumber,
    useColorMode,
} from '@chakra-ui/react';
import React from 'react';
import { IObject } from '../../types/bet-split';
import { formatCurrency } from '../../utils/format';
import * as icons from '../icons';
import Text from '../text';

interface WidgetRowProps {
    betObject: IObject;
}

function WidgetRow(props: WidgetRowProps) {
    const { betObject } = props;
    const { colorMode } = useColorMode();

    const Icon =
        icons[`${betObject.bets[0].event.sport}Icon` as keyof typeof icons];

    return (
        <Flex w="full" direction="column" gap="16px">
            <Flex align="center" justify="space-between">
                <Text size="sm">May (0 - 0)</Text>
                <Text size="md" lineHeight="16px" color="red" as="strong">
                    $243.82
                </Text>
            </Flex>
            <Card
                shadow="none"
                bg={colorMode == 'light' ? 'skySoft' : 'alphaWhite003'}
                borderRadius="8px"
            >
                <CardBody p={{ base: '30px 12px', md: '30px' }}>
                    <Flex
                        align="center"
                        mb="24px"
                        gap="16px"
                        flexWrap={{ base: 'wrap', md: 'nowrap' }}
                    >
                        <Flex
                            align="center"
                            justify="center"
                            w="50px"
                            minW="50px"
                            h="50px"
                            rounded="full"
                            bg="skyBlue"
                        >
                            <Icon />
                        </Flex>
                        <Text
                            fontSize="14px"
                            lineHeight="16px"
                            maxW={{ base: 'calc(100% - 66px)', md: 'full' }}
                        >
                            {betObject.bets[0].bookDescription}
                        </Text>
                        <Flex
                            direction={{ base: 'row', md: 'column' }}
                            align={{ base: 'center', md: 'flex-end' }}
                            justify={{
                                base: 'space-between',
                                md: 'flex-start',
                            }}
                            gap="6px"
                            ml="auto"
                            w={{ base: 'full', md: 'auto' }}
                        >
                            <Box
                                px="10px"
                                py="6px"
                                bg={
                                    colorMode == 'light'
                                        ? 'rgba(120, 184, 244, 0.1)'
                                        : 'alphaWhite01'
                                }
                                border="1px"
                                borderColor={
                                    colorMode == 'light'
                                        ? 'rgba(36, 53, 106, 0.08)'
                                        : 'alphaWhite005'
                                }
                                borderRadius="4px"
                            >
                                <Text size="sm" whiteSpace="nowrap">
                                    KC 38 @ PHI 35
                                </Text>
                            </Box>
                            <Text size="sm">Final 2/12</Text>
                        </Flex>
                    </Flex>
                    <Flex
                        bg={colorMode == 'light' ? 'skySoft' : 'alphaWhite005'}
                        border="1px"
                        borderColor={
                            colorMode == 'light'
                                ? 'rgba(36, 53, 106, 0.08)'
                                : 'alphaWhite008'
                        }
                        borderRadius="6px"
                        gap={{ base: '0', md: '10px' }}
                    >
                        <Flex
                            w="full"
                            maxW={{ base: '80px', md: '120px' }}
                            minW={{ base: '80px', md: '120px' }}
                            align="center"
                            justify="center"
                            p={{ base: '0 12px', md: '12px' }}
                        >
                            {betObject.book.logo ? (
                                <Image src={betObject.book.logo} />
                            ) : (
                                <Text>{betObject.book.name}</Text>
                            )}
                        </Flex>
                        <Flex w="full" gap={{ base: '0', md: '10px' }}>
                            <Stat
                                w="full"
                                maxW={{ base: '84px', md: 'unset' }}
                                p={{ base: '24px 6px', md: '24px' }}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <StatLabel>At Risk</StatLabel>
                                <StatNumber>
                                    <Text as="b" lineHeight="inherit">
                                        {formatCurrency(betObject.atRisk)}
                                    </Text>
                                </StatNumber>
                            </Stat>
                            <Stat
                                w="full"
                                maxW={{ base: '84px', md: 'unset' }}
                                p={{ base: '24px 6px', md: '24px' }}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <StatLabel>Odds</StatLabel>
                                <StatNumber>
                                    {betObject.oddsAmerican || '-'}
                                </StatNumber>
                            </Stat>
                            {betObject.status === 'pending' && (
                                <Stat
                                    w="full"
                                    maxW={{ base: '84px', md: 'unset' }}
                                    p={{ base: '24px 6px', md: '24px' }}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <StatLabel>To Win</StatLabel>
                                    <StatNumber>
                                        <Text as="b" lineHeight="inherit">
                                            {formatCurrency(betObject.toWin)}
                                        </Text>
                                    </StatNumber>
                                </Stat>
                            )}
                            {betObject.netProfit &&
                                betObject.status !== 'pending' && (
                                    <Stat
                                        w="full"
                                        maxW={{ base: '84px', md: 'unset' }}
                                        p={{ base: '24px 6px', md: '24px' }}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <StatLabel>
                                            {betObject.status === 'won'
                                                ? 'Won'
                                                : 'Lost'}
                                        </StatLabel>
                                        <StatNumber
                                            color={
                                                betObject.status === 'won'
                                                    ? 'green'
                                                    : 'red'
                                            }
                                        >
                                            <Text as="b" lineHeight="inherit">
                                                {formatCurrency(
                                                    betObject.netProfit
                                                )}
                                            </Text>
                                        </StatNumber>
                                    </Stat>
                                )}
                        </Flex>
                    </Flex>
                </CardBody>
            </Card>
            <Flex align="center" gap="16px">
                <Text size="sm" color="red" whiteSpace="nowrap">
                    100% Lost
                </Text>
                <Box w="full" h="3px" borderRadius="3px" bg="red"></Box>
            </Flex>
        </Flex>
    );
}

export default WidgetRow;
